import { cn } from '../lib/utils.js';

// Status pill shared by the list pages (forms, proptrees) so "Active"
// looks identical wherever it appears. A dot plus an uppercase mono
// label; green when the thing is live, muted otherwise.
export function StatusBadge({
  label,
  tone,
  className,
}: {
  label: string;
  tone: 'success' | 'muted';
  className?: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.06em]',
        tone === 'success'
          ? 'border-success/30 bg-success/10 text-success'
          : 'border-border bg-muted/30 text-muted-foreground',
        className,
      )}
    >
      <span
        className={cn(
          'h-1.5 w-1.5 rounded-full',
          tone === 'success' ? 'bg-success' : 'bg-muted-foreground/60',
        )}
      />
      {label}
    </span>
  );
}

// Statuses that mean "live". Proptree rows carry a free-text status
// (the agent's status name, or the proptree's own), so match on value
// rather than assuming a boolean.
const ACTIVE = new Set(['active', 'enabled']);

export function isActiveStatus(label: string | null | undefined): boolean {
  return typeof label === 'string' && ACTIVE.has(label.trim().toLowerCase());
}

// Display label for a stored status. The DB says 'enabled'; the UI says
// "Active" everywhere, so normalise here rather than at each call site.
export function statusLabel(status: string | null | undefined): string {
  if (!status) return '—';
  const v = status.trim().toLowerCase();
  if (v === 'enabled' || v === 'active') return 'Active';
  if (v === 'disabled') return 'Disabled';
  return status.charAt(0).toUpperCase() + status.slice(1);
}
